import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { schoolApi, type SchoolDto } from '../../api/school'
import { queryKeys } from '../../api/queryKeys'

/**
 * Политика безопасности школы (6.1): «2FA обязательна для всех сотрудников».
 * Для директора/завуча/админов 2FA обязательна всегда; флаг распространяет
 * требование на обычных учителей. Меняет только директор (сервер проверяет).
 */
export function SchoolSecuritySettings({ school }: { school: SchoolDto }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const enabled = school.require2faAll === true

  const toggle = useMutation({
    mutationFn: (value: boolean) => schoolApi.setRequire2faAll(school.id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.school.byId(school.id) }),
  })

  const onChange = (value: boolean) => {
    if (value && !confirm(t('schoolSecurity.enableConfirm'))) return
    toggle.mutate(value)
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 text-sm">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
        {t('schoolSecurity.title')}
      </div>
      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          className="mt-1"
          checked={enabled}
          disabled={toggle.isPending}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>
          <span className="font-medium text-gray-900">{t('schoolSecurity.require2faAll')}</span>
          <span className="block text-xs text-gray-500 mt-0.5">{t('schoolSecurity.hint')}</span>
        </span>
      </label>
      {toggle.error && (
        <div role="alert" className="text-xs text-red-600 mt-2">{(toggle.error as Error).message}</div>
      )}
    </div>
  )
}
